import { useEffect, useMemo, useState } from 'react';
import { UniversalPage, Card, NotificationCard } from '@sighthost/ui';
import {
  adminReportsService,
  type AcquisitionSummaryRow,
} from '@/domains/reports/services/adminReportsService';

/**
 * AcquisitionPage — M3.16 admin report for "where do customers come from?".
 *
 * Reads `v_admin_acquisition_summary` (aggregates customer_profile.acquisition_source
 * by source/medium/campaign/content). Shows:
 *  - 4 headline KPI cards (total UTM-attributed signups, conversion to service,
 *    active customers, distinct campaigns)
 *  - Sortable detail table grouped by source / medium / campaign / content
 *
 * UTM data is captured at sign-up by SignUpPage (M3.15) — flows from
 * marketing-site CTAs (M3.14) → URL searchParams → user_metadata →
 * handle_new_user trigger → customer_profile.acquisition_source. Direct /
 * staff / import-flow signups have NULL acquisition_source and don't appear.
 */
export default function AcquisitionPage() {
  const [rows, setRows] = useState<AcquisitionSummaryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminReportsService
      .getAcquisitionSummary()
      .then(setRows)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  const kpis = useMemo(() => {
    if (!rows) return null;
    const totalSignups = rows.reduce((s, r) => s + r.signups, 0);
    const totalWithService = rows.reduce(
      (s, r) => s + r.customers_with_service,
      0,
    );
    const totalActive = rows.reduce((s, r) => s + r.active_customers, 0);
    const distinctCampaigns = new Set(
      rows.map((r) => r.campaign).filter((c): c is string => Boolean(c)),
    ).size;
    const conversionPct =
      totalSignups > 0
        ? Math.round((totalWithService / totalSignups) * 100)
        : 0;
    return {
      totalSignups,
      totalWithService,
      totalActive,
      distinctCampaigns,
      conversionPct,
    };
  }, [rows]);

  return (
    <UniversalPage
      title="Customer acquisition"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Reports', href: '/admin/reports' },
        { label: 'Acquisition' },
      ]}
    >
      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load acquisition summary"
          body={error}
        />
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Marketing signups
          </p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {kpis?.totalSignups ?? '…'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            UTM-attributed only
          </p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Converted to service
          </p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {kpis?.totalWithService ?? '…'}
          </p>
          {kpis && (
            <p className="mt-1 text-xs text-muted-foreground">
              {kpis.conversionPct}% of marketing signups
            </p>
          )}
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Active customers
          </p>
          <p className="mt-2 text-2xl font-semibold text-success">
            {kpis?.totalActive ?? '…'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            lifecycle ≠ lead/churned
          </p>
        </Card>
        <Card interactive={false} className="p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Distinct campaigns
          </p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {kpis?.distinctCampaigns ?? '…'}
          </p>
        </Card>
      </div>

      {/* Detail table */}
      <Card interactive={false} className="overflow-hidden">
        <div className="border-b border-border bg-muted/30 px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Breakdown</h3>
          <p className="text-xs text-muted-foreground">
            Each row = one (source × medium × campaign × content) combination.
            Click "Marketing signups" to see what's tagged on the marketing site.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-muted/20 text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-muted-foreground">
                  Source
                </th>
                <th className="px-4 py-2 font-medium text-muted-foreground">
                  Medium
                </th>
                <th className="px-4 py-2 font-medium text-muted-foreground">
                  Campaign
                </th>
                <th className="px-4 py-2 font-medium text-muted-foreground">
                  Content
                </th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                  Signups
                </th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                  With service
                </th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                  Active
                </th>
                <th className="px-4 py-2 font-medium text-muted-foreground">
                  Most recent
                </th>
              </tr>
            </thead>
            <tbody>
              {!rows && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-sm text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {rows && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-6 text-center text-sm text-muted-foreground"
                  >
                    No UTM-attributed signups yet. Marketing-site CTAs need to
                    be clicked + signups completed before data appears here.
                  </td>
                </tr>
              )}
              {rows?.map((r, i) => (
                <tr
                  key={`${r.source}-${r.medium}-${r.campaign}-${r.content}-${i}`}
                  className="border-t border-border hover:bg-muted/20"
                >
                  <td className="px-4 py-2 text-foreground">{r.source ?? '—'}</td>
                  <td className="px-4 py-2 text-foreground">{r.medium ?? '—'}</td>
                  <td className="px-4 py-2 text-foreground">{r.campaign ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground">
                    {r.content ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">
                    {r.signups}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {r.customers_with_service}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-success">
                    {r.active_customers}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {r.most_recent_signup_at
                      ? new Date(r.most_recent_signup_at).toLocaleDateString()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Data flow: marketing CTA URL params (utm_*) → portal sign-up →{' '}
          <code>customer_profile.acquisition_source</code> JSONB column.
          Aggregated via <code>v_admin_acquisition_summary</code> view. Zero
          third-party trackers — UTM attribution captured first-party at the
          consent moment.
        </p>
      </Card>
    </UniversalPage>
  );
}
